import { useMutation } from '@apollo/client';
import _ from 'lodash';
import React from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import Form, { Field } from '../components/Form';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import { SidebarItem } from '../graphql/generated/graphql';
import styles from './SidebarSettings.module.scss';
import useExternalPostPlatforms from './useExternalPostPlatforms';
import useSidebarSettings, {
  updateSidebarSettingsCache,
} from './useSidebarSettings';
import { gql } from '../graphql/generated/gql';

const HIDE_SIDEBAR_ITEMS_MUTATION = gql(`
  mutation HideSidebarItems($hiddenItems: [SidebarItem!]!) {
    updateUserSettings(sidebar: { hiddenItems: $hiddenItems }) {
      sidebar {
        hiddenItems
      }
    }
  }
`);

interface SidebarSettingsFieldProps {
  name: SidebarItem;
  label: string;
  onToggleItem: (name: SidebarItem) => void;
  rightLink?: string;
  rightOnClick?: () => void;
}

const SidebarSettingsField = ({
  name,
  label,
  onToggleItem,
  rightLink,
  rightOnClick,
}: SidebarSettingsFieldProps) => {
  return (
    <div className={styles.field}>
      <Field
        name={name}
        type="checkbox"
        onChange={() => onToggleItem(name)}
        label={label}
        margin="none"
      />
      {(rightLink || rightOnClick) && (
        <Button
          className={styles.caretLink}
          icon={
            <div className={styles.caret}>
              <Caret block direction="right" />
            </div>
          }
          to={rightLink}
          onClick={rightOnClick}
          aria-label={`Go to ${label}`}
        />
      )}
    </div>
  );
};

interface SidebarSettingsProps {
  close: () => void;
}

const SidebarSettings = ({ close }: SidebarSettingsProps) => {
  const [hideItems] = useMutation(HIDE_SIDEBAR_ITEMS_MUTATION, {
    update: updateSidebarSettingsCache,
  });

  const { hiddenItems } = useSidebarSettings();

  const platforms = useExternalPostPlatforms();

  const userType = useAppSelector((state) => state.session.user.type);
  const isGuest = userType === 'GUEST';

  const handleToggleItem = (name: SidebarItem) => {
    const newHiddenItems = hiddenItems.has(name)
      ? _.without<SidebarItem>([...hiddenItems], name)
      : [...hiddenItems, name];

    hideItems({
      variables: { hiddenItems: newHiddenItems },
      optimisticResponse: {
        updateUserSettings: {
          __typename: 'UserSettings',
          sidebar: {
            __typename: 'UserSidebarSettings',
            hiddenItems: newHiddenItems,
          },
        },
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <PopOverContainer noPadding width="auto">
        <div className={styles.content}>
          <div className={styles.header}>Show in Sidebar</div>
          <Form
            initialValues={_.fromPairs(
              Object.values(SidebarItem).map((sidebarItem) => [
                sidebarItem,
                !hiddenItems.has(sidebarItem),
              ]),
            )}
          >
            <SidebarSettingsField
              name={SidebarItem.Templates}
              label="Templates"
              onToggleItem={handleToggleItem}
              rightLink="/templates"
              rightOnClick={() => {
                close();
              }}
            />
            {!isGuest && (
              <SidebarSettingsField
                name={SidebarItem.ContentMap}
                label="Content Map"
                onToggleItem={handleToggleItem}
                rightLink="/topics"
                rightOnClick={() => close()}
              />
            )}
            <SidebarSettingsField
              name={SidebarItem.Groups}
              label="Groups"
              onToggleItem={handleToggleItem}
              rightLink="/groups"
              rightOnClick={() => close()}
            />
            {/* TODO: uncomment once we have insights in sidebar
            {isAdmin && (
              <SidebarSettingsField
                name={SidebarItem.Insights}
                label="Insights"
                onToggleItem={handleToggleItem }
                rightLink="/admin/insights"
                rightOnClick={() => close()}
              />
            )} */}
            <SidebarSettingsField
              name={SidebarItem.AllTopics}
              label="All Topics"
              onToggleItem={handleToggleItem}
            />
            {!isGuest && (
              <SidebarSettingsField
                name={SidebarItem.MyTopics}
                label="My Topics"
                onToggleItem={handleToggleItem}
              />
            )}
            {platforms.length > 0 && (
              <SidebarSettingsField
                name={SidebarItem.Apps}
                label="Integrations"
                onToggleItem={handleToggleItem}
              />
            )}
          </Form>
        </div>
      </PopOverContainer>
    </div>
  );
};

export default SidebarSettings;
